import { ArrowLeft, Menu } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export interface NavItem {
    title: string;
    icon: LucideIcon;
    path: string;
}

export interface PageSidebarProps {
    title: string;
    navItems: NavItem[];
    basePath: string;
    backLink: {
        label: string;
        href: string;
    };
    isActive: (path: string) => boolean;
    /**
     * Keep the sidebar in view while the page body scrolls (desktop
     * only — the mobile menu button scrolls with the content).
     */
    pinned?: boolean;
}

function NavContent({
    navItems,
    basePath,
    backLink,
    isActive,
    onNavigate,
}: PageSidebarProps & { onNavigate?: () => void }) {
    return (
        <nav className="flex flex-col gap-1">
            {/* Back Link */}
            <Button
                variant="ghost"
                asChild
                className="justify-start text-muted-foreground"
                onClick={onNavigate}
            >
                <Link to={backLink.href}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {backLink.label}
                </Link>
            </Button>
            <Separator className="my-2" />
            {/* Navigation Items */}
            {navItems.map((item) => (
                <Button
                    key={item.path}
                    variant="ghost"
                    asChild
                    className={cn('justify-start', isActive(item.path) && 'bg-muted font-medium')}
                    onClick={onNavigate}
                >
                    <Link to={item.path ? `${basePath}/${item.path}` : basePath}>
                        <item.icon className="mr-2 h-4 w-4" />
                        {item.title}
                    </Link>
                </Button>
            ))}
        </nav>
    );
}

export function PageSidebar({ pinned = true, ...props }: PageSidebarProps) {
    const [open, setOpen] = useState(false);

    return (
        <>
            {/* Mobile: Menu button + Sheet */}
            <div className="md:hidden mb-4">
                <Sheet open={open} onOpenChange={setOpen}>
                    <SheetTrigger asChild>
                        <Button variant="outline" size="sm">
                            <Menu className="mr-2 h-4 w-4" />
                            Menu
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-64">
                        <SheetHeader>
                            <SheetTitle>{props.title}</SheetTitle>
                        </SheetHeader>
                        <div className="mt-4">
                            <NavContent {...props} onNavigate={() => setOpen(false)} />
                        </div>
                    </SheetContent>
                </Sheet>
            </div>

            {/* Desktop: Sidebar. `self-start` is what makes `sticky`
                effective — flex stretch would otherwise make the aside
                full-height, leaving it nothing to stick against. */}
            <aside
                className={cn(
                    'hidden md:block w-56 shrink-0',
                    // top-0 on purpose: browsers add the sticky offset
                    // ON TOP of the scroll container's padding, so
                    // PageBody's p-4 already provides the visual gap.
                    // A non-zero top would push the sidebar down by
                    // that amount — but only on pages tall enough to
                    // scroll, making the offset content-dependent.
                    pinned && 'md:sticky md:top-0 md:self-start'
                )}
            >
                <NavContent {...props} />
            </aside>
        </>
    );
}
